import { useLazyQuery, useMutation, useQuery } from '@apollo/react-hooks';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  ListSubheader,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { MuiLink } from 'components/Link';
import gql from 'graphql-tag';
import ListItem from 'shared/components/ListItem';
import { chain, sortBy, startCase, ObjectChain } from 'lodash';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import {
  Button,
  Caption,
  Card,
  List,
  Paragraph,
  Subheading,
} from 'react-native-paper';
import { useLocation } from 'react-router-dom';
import { colors } from 'shared/styles';

interface AppSubscription {
  appPrice?: AppPrice;
  active: boolean;
  statusMessage: string;
  status: string;
  store?: string;
  tiered?: boolean;
  locked?: boolean;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  currentActiveUsersCount: number;
}

interface CurrentUserData {
  hasStripeId: boolean;
  appSubscription: AppSubscription;
}

interface AppPrice {
  id: string;
  amountCents: number;
  name: string;
  maxUsers: number;
  recurring: string;
  moneyDisplay: string;
}

interface TierChangePreview {
  amountDueCents: number;
  amountDueDisplay: string;
  subtotalCents: number;
  totalCents: number;
  taxCents: number | null;
  currency: string;
  now: boolean;
}
interface QueryData {
  currentUser: CurrentUserData;
  availableAppPrices: AppPrice[];
  tieredAppPrices: AppPrice[];
  premiumAppPrices: AppPrice[];
}

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Stripe/webhook can lag behind the tier-change mutation; refetch until the subscription price id matches. */
async function pollRefetchUntilAppPriceId(
  refetch: () => Promise<{ data?: QueryData }>,
  expectedAppPriceId: string,
  options: {
    shouldContinue?: () => boolean;
  } = {}
): Promise<boolean> {
  const maxAttempts = 10;
  const shouldContinue = options.shouldContinue ?? (() => true);
  const delayAfterAttempt = (attempt: number) =>
    Math.min(500 + attempt * 100, 1000);

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (!shouldContinue()) {
      return false;
    }
    const { data } = await refetch();
    const id = data?.currentUser?.appSubscription?.appPrice?.id;
    if (id === expectedAppPriceId) {
      return true;
    }
    if (attempt < maxAttempts - 1) {
      await sleep(delayAfterAttempt(attempt));
    }
  }
  return false;
}

const CREATE_PORTAL_SESSION = gql`
  mutation CreateAppBillingPortalSession {
    url: appBillingPortalCreate
  }
`;

const CHECKOUT_CREATION = gql`
  mutation CreateAppCheckout($input: AppPriceCheckoutInput!) {
    checkout: appPriceCheckout(input: $input) {
      url
    }
  }
`;

const TIER_CHANGE = gql`
  mutation AppSubscriptionTierChange($appPriceId: ID!) {
    user: appSubscriptionTierChange(appPriceId: $appPriceId) {
      id
    }
  }
`;

const TIER_CHANGE_PREVIEW = gql`
  query AppSubscriptionTierChangePreview($appPriceId: ID!) {
    appSubscriptionTierChangePreview(appPriceId: $appPriceId) {
      amountDueCents
      amountDueDisplay
      subtotalCents
      totalCents
      taxCents
      currency
      now
    }
  }
`;

const APP_PRICE_FRAGMENT = gql`
  fragment AppPriceFragment on AppPrice {
    id
    amountCents
    recurring
    name
    maxUsers
    moneyDisplay
  }
`;

const GET_BILLING = gql`
  query GetBillingInfo {
    currentUser {
      id
      hasStripeId
      appSubscription {
        active
        store
        tiered
        locked
        currentActiveUsersCount
        currentPeriodStart
        currentPeriodEnd
        status
        statusMessage
        appPrice {
          ...AppPriceFragment
        }
      }
    }
    availableAppPrices {
      ...AppPriceFragment
    }
    tieredAppPrices: appPrices(tiered: true) {
      ...AppPriceFragment
    }
    premiumAppPrices: appPrices(tiered: false) {
      ...AppPriceFragment
    }
  }
  ${APP_PRICE_FRAGMENT}
`;

const formatMonthly = (price: AppPrice) => `${price.moneyDisplay} /mo`;
const formatAnnual = (price: AppPrice) => `${price.moneyDisplay} /yr`;
const formatPrice = (price: AppPrice) => {
  if (price.recurring === 'monthly') {
    return formatMonthly(price);
  } else if (price.recurring === 'annual') {
    return formatAnnual(price);
  } else {
    return `${price.moneyDisplay} ${price.recurring}`;
  }
};
const getCurrency = (moneyDisplay: string) =>
  moneyDisplay.replace(/[0-9,]/g, '');

const formatCents = (cents: number, currency: string) =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
  }).format(cents / 100);

/** Prefer next tier up by seat limit; else closest tier down. */
function defaultAdjacentTierName(
  tieredPrices: AppPrice[],
  currentAppPrice: AppPrice
): string | undefined {
  if (tieredPrices.length === 0) return undefined;
  const byMaxUsers = chain(tieredPrices)
    .groupBy('name')
    .map((prices, name) => ({ name, maxUsers: prices[0].maxUsers }))
    .sortBy('maxUsers')
    .value();
  const currentMax = currentAppPrice.maxUsers;
  const upgrade = byMaxUsers.find((x) => x.maxUsers > currentMax);
  if (upgrade) return upgrade.name;
  const downgrade = [...byMaxUsers]
    .reverse()
    .find((x) => x.maxUsers < currentMax);
  if (downgrade) return downgrade.name;
  return byMaxUsers[0]?.name;
}

function morePlansDisplay(lastPrice: ObjectChain<AppPrice>): React.ReactNode {
  const currency = getCurrency(lastPrice.get('moneyDisplay').value() || '$');
  return `More than ${lastPrice
    .get('maxUsers')
    .value()} users: ${currency}30/mo or ${currency}300/yr per 100 additional users. `;
}

const PricesSelector = ({
  premiumPrices,
  prices,
  checkout,
  loadingCheckout,
}: {
  premiumPrices: AppPrice[];
  prices: AppPrice[];
  checkout: (price: AppPrice) => void;
  loadingCheckout: boolean;
}) => {
  const [planName, setPlanName] = useState<string>();
  const allPrices = [...prices, ...premiumPrices];
  const priceOptions = allPrices.filter((p) => p.name === planName);

  return (
    <Card style={styles.card}>
      <Card.Title title="Plan" />
      <Card.Content>
        <FormControl
          variant="outlined"
          style={{ marginRight: 10, minWidth: 80, maxWidth: 250 }}
          margin="dense"
        >
          <InputLabel htmlFor="tier-select">Choose Plan</InputLabel>

          <Select
            value={planName}
            onChange={({ target }) => setPlanName(target.value as string)}
            id="tier-select"
            label="Choose Plan"
          >
            <ListSubheader>Individual</ListSubheader>
            {chain(premiumPrices)
              .groupBy('name')
              .map((prices, name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))
              .value()}
            <ListSubheader>Groups</ListSubheader>
            {chain(prices)
              .groupBy('name')
              .map((prices, name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))
              .value()}
          </Select>
        </FormControl>

        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          {sortBy(priceOptions, (p) => p.recurring)
            .reverse()
            .map((price) => (
              <List.Item
                key={price.id}
                title={formatPrice(price)}
                titleStyle={{ width: 100 }}
                description={
                  price.recurring === 'annual' ? '2 months free' : ''
                }
                left={() => (
                  <Button
                    compact
                    mode="contained"
                    style={{ alignSelf: 'center', width: 200 }}
                    disabled={loadingCheckout}
                    onPress={() => checkout(price)}
                  >
                    {`Choose ${price.recurring}`}
                  </Button>
                )}
              />
            ))}
        </View>
      </Card.Content>
    </Card>
  );
};

const GroupTierChangeSelector = ({
  tieredPrices,
  currentAppPrice,
  usersCount,
  currentPeriodEnd,
  refetchBilling,
  onTierPlanChangeDetected,
  onTierPlanChangeBannerDismiss,
}: {
  tieredPrices: AppPrice[];
  currentAppPrice: AppPrice;
  usersCount: number;
  currentPeriodEnd?: string | null;
  refetchBilling: () => Promise<{ data?: QueryData }>;
  onTierPlanChangeDetected: (message: string) => void;
  onTierPlanChangeBannerDismiss: () => void;
}) => {
  const pollBillingActiveRef = useRef(true);
  useEffect(() => {
    pollBillingActiveRef.current = true;
    return () => {
      pollBillingActiveRef.current = false;
    };
  }, []);

  const [expanded, setExpanded] = useState(false);
  const [planName, setPlanName] = useState<string>();
  const [tierChangeError, setTierChangeError] = useState<string | null>(null);
  const [tierDialogOpen, setTierDialogOpen] = useState(false);
  const [tierDialogPrice, setTierDialogPrice] = useState<AppPrice | null>(null);

  const [
    fetchTierPreview,
    {
      data: tierPreviewData,
      loading: tierPreviewLoading,
      error: tierPreviewQueryError,
    },
  ] = useLazyQuery<{ appSubscriptionTierChangePreview: TierChangePreview }>(
    TIER_CHANGE_PREVIEW,
    { fetchPolicy: 'network-only' }
  );

  const closeTierChangeDialog = () => {
    setTierDialogOpen(false);
    setTierDialogPrice(null);
  };

  const [tierChange, { loading: tierLoading, error: tierMutationError }] =
    useMutation(TIER_CHANGE, {
      onCompleted: () => setTierChangeError(null),
    });

  const tierGraphQLErrorMessage =
    tierMutationError &&
    tierMutationError.graphQLErrors &&
    tierMutationError.graphQLErrors[0] &&
    tierMutationError.graphQLErrors[0].message;

  const tierPreview = tierPreviewData?.appSubscriptionTierChangePreview;
  const previewGraphQLError =
    tierPreviewQueryError &&
    tierPreviewQueryError.graphQLErrors &&
    tierPreviewQueryError.graphQLErrors[0] &&
    tierPreviewQueryError.graphQLErrors[0].message;

  const selectionBlocked = tierDialogOpen || tierLoading;

  const isTierDowngrade = !tierPreview?.now;

  const openTierChangeDialog = (price: AppPrice) => {
    setTierChangeError(null);
    onTierPlanChangeBannerDismiss();
    if (
      price.maxUsers < currentAppPrice.maxUsers &&
      usersCount > price.maxUsers
    ) {
      setTierChangeError(
        `You have ${usersCount} active members. Choose a tier with enough seats, or remove members first.`
      );
      return;
    }
    setTierDialogPrice(price);
    setTierDialogOpen(true);
    fetchTierPreview({ variables: { appPriceId: price.id } });
  };

  const confirmTierChange = () => {
    if (!tierDialogPrice) {
      return;
    }
    const expectedAppPriceId = tierDialogPrice.id;
    const successPlanSummary = `${tierDialogPrice.name} — ${formatPrice(
      tierDialogPrice
    )}`;
    tierChange({ variables: { appPriceId: tierDialogPrice.id } })
      .then(async (data) => {
        closeTierChangeDialog();
        if (!data.errors) {
          setExpanded(false);
          const detected = await pollRefetchUntilAppPriceId(
            refetchBilling,
            expectedAppPriceId,
            {
              shouldContinue: () => pollBillingActiveRef.current,
            }
          );
          if (detected) {
            onTierPlanChangeDetected(
              `Your plan was updated to ${successPlanSummary}.`
            );
          }
        }
      })
      .catch(() => undefined);
  };

  useEffect(() => {
    if (expanded) {
      setPlanName(undefined);
    }
  }, [expanded]);

  if (tieredPrices.length === 0) {
    return null;
  }

  const names = chain(tieredPrices)
    .groupBy('name')
    .map((prices, name) => ({ name, maxUsers: prices[0].maxUsers }))
    .sortBy('maxUsers')
    .map((x) => x.name)
    .value();
  const selectedName =
    planName ??
    defaultAdjacentTierName(tieredPrices, currentAppPrice) ??
    names[0];
  const priceOptions = sortBy(
    tieredPrices.filter((p) => p.name === selectedName),
    (p) => p.recurring
  ).reverse();

  const expandTierSection = () => {
    onTierPlanChangeBannerDismiss();
    setExpanded(true);
  };

  const expandButton = (
    <Button
      compact
      style={{ minWidth: 151 }}
      icon="swap-horizontal"
      mode="contained"
      onPress={expandTierSection}
    >
      Update tier
    </Button>
  );

  const expandedBody = (
    <>
      {tierChangeError && (
        <Alert severity="warning" style={{ marginBottom: 12 }}>
          {tierChangeError}
        </Alert>
      )}
      {tierGraphQLErrorMessage && (
        <Alert severity="error" style={{ marginBottom: 12 }}>
          {tierGraphQLErrorMessage}
        </Alert>
      )}
      <Caption style={{ marginBottom: 12 }}>
        Move to a higher tier for more seats, or a lower one if your active
        member count fits. For upgrades, review the prorated charge (from
        Stripe, including tax where applicable) before confirming—your saved
        card is charged that amount immediately. Downgrades apply without
        proration. Your billing period stays the same (
        {startCase(currentAppPrice.recurring)}).
      </Caption>
      <View
        style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          marginTop: 8,
        }}
      >
        <FormControl
          variant="outlined"
          style={{ marginRight: 10, minWidth: 80, maxWidth: 280 }}
          margin="dense"
        >
          <InputLabel htmlFor="tier-change-select">Tier</InputLabel>
          <Select
            value={selectedName || ''}
            onChange={({ target }) => setPlanName(target.value as string)}
            id="tier-change-select"
            label="Tier"
          >
            {names.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {priceOptions.map((price) => {
          const downgradeBlocked =
            price.maxUsers < currentAppPrice.maxUsers &&
            usersCount > price.maxUsers;
          return (
            <List.Item
              key={price.id}
              titleStyle={{ minWidth: 120 }}
              title={
                downgradeBlocked
                  ? `Need at most ${price.maxUsers} active members`
                  : price.recurring === 'annual'
                    ? '2 months free vs monthly'
                    : ''
              }
              left={() => (
                <Button
                  compact
                  mode="contained"
                  style={{
                    alignSelf: 'center',
                    width: 200,
                    marginRight: 8,
                  }}
                  disabled={selectionBlocked || downgradeBlocked}
                  onPress={() => openTierChangeDialog(price)}
                >
                  Review change
                </Button>
              )}
            />
          );
        })}
      </View>
      <Button
        mode="text"
        style={{ marginTop: 16, alignSelf: 'flex-start' }}
        onPress={() => setExpanded(false)}
      >
        Cancel
      </Button>
    </>
  );

  return (
    <>
      {!expanded ? (
        <ListItem
          title="Tier Change"
          description="Choose a different seat limit for your group. You'll see any amount due from Stripe before you confirm."
          descriptionNumberOfLines={0}
          onPress={expandTierSection}
          style={{ paddingHorizontal: 0, paddingVertical: 4 }}
          right={(props) => (
            <View
              style={[
                props.style,
                { justifyContent: 'center', marginLeft: 10 },
              ]}
            >
              {expandButton}
            </View>
          )}
        />
      ) : (
        <ListItem
          title="Tier Change"
          descriptionNumberOfLines={0}
          description={<View>{expandedBody}</View>}
        />
      )}

      <Dialog
        open={tierDialogOpen}
        onClose={closeTierChangeDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm tier change</DialogTitle>
        <DialogContent>
          <View>
            {tierPreviewLoading ? (
              <CircularProgress
                style={{ display: 'block', margin: '16px auto' }}
              />
            ) : (
              <>
                <Alert severity="info" style={{ marginBottom: 16 }}>
                  {isTierDowngrade ? (
                    <>
                      <strong>No proration on downgrades.</strong> The new tier
                      takes effect without a time-based credit or extra charge
                      for the remainder of this period.
                    </>
                  ) : (
                    <>
                      <strong>Today&apos;s charge is prorated.</strong> You pay
                      only the difference for the rest of your current billing
                      period—not the full plan price.
                    </>
                  )}
                </Alert>
                {tierDialogPrice && (
                  <Paragraph style={{ marginBottom: 12 }}>
                    <strong>New plan</strong>: {tierDialogPrice.name} —{' '}
                    {formatPrice(tierDialogPrice)}
                  </Paragraph>
                )}
                {tierDialogPrice && currentPeriodEnd && (
                  <Paragraph style={{ marginBottom: 12 }}>
                    <strong>Next full charge</strong>:{' '}
                    {formatPrice(tierDialogPrice)} on{' '}
                    <strong>{moment(currentPeriodEnd).format('LL')}</strong>{' '}
                    (when your current billing period renews). Additional taxes
                    may apply on that invoice.
                  </Paragraph>
                )}
                {previewGraphQLError && (
                  <Alert severity="error" style={{ marginBottom: 12 }}>
                    {previewGraphQLError}
                  </Alert>
                )}
                {tierPreview && (
                  <>
                    <Paragraph
                      style={{
                        marginBottom: 8,
                        padding: 12,
                        borderRadius: 4,
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      }}
                    >
                      <strong>
                        {isTierDowngrade
                          ? 'Amount due now'
                          : 'Prorated amount due now'}
                      </strong>
                      : {tierPreview.amountDueDisplay}
                    </Paragraph>
                    {tierPreview.taxCents != null &&
                      tierPreview.taxCents > 0 && (
                        <Caption style={{ marginTop: 4 }}>
                          Subtotal{' '}
                          {formatCents(
                            tierPreview.subtotalCents,
                            tierPreview.currency
                          )}
                          {' · '}Tax{' '}
                          {formatCents(
                            tierPreview.taxCents,
                            tierPreview.currency
                          )}
                        </Caption>
                      )}
                    <Caption style={{ marginTop: 12 }}>
                      {tierPreview.amountDueCents > 0
                        ? 'Stripe charges your default payment method this amount as soon as you confirm. Totals reflect Stripe’s preview and may differ slightly from what appears on your bank statement because of currency conversion or when the charge actually settles.'
                        : isTierDowngrade
                          ? 'No charge for this tier change. Your next renewal will bill at the new tier price.'
                          : 'No payment is collected now. Proration may appear as credit on your next invoice.'}
                    </Caption>
                  </>
                )}
              </>
            )}
          </View>
        </DialogContent>
        <DialogActions>
          <Button mode="text" onPress={closeTierChangeDialog}>
            Cancel
          </Button>
          <Button
            mode="contained"
            disabled={
              tierPreviewLoading ||
              !tierPreview ||
              !!previewGraphQLError ||
              tierLoading
            }
            loading={tierLoading}
            onPress={confirmTierChange}
          >
            Confirm change
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const BillingPage = () => {
  const location = useLocation();

  const stripeSuccess = location.search.includes('stripeSuccess=true');

  const { data, refetch } = useQuery<QueryData>(GET_BILLING, {
    fetchPolicy: 'cache-and-network',
    pollInterval: stripeSuccess ? 5000 : undefined,
  });

  const [loadingCheckout, setLoadingCheckout] = useState(false);
  const [createCheckout] = useMutation(CHECKOUT_CREATION, {});
  const [createPortalSession, { loading: loadingPortal }] = useMutation(
    CREATE_PORTAL_SESSION
  );
  const [tierPlanChangeSuccess, setTierPlanChangeSuccess] = useState<
    string | null
  >(null);

  const openBillingPortal = () => {
    createPortalSession().then(({ data }) => {
      if (data) window.location.href = data.url;
    });
  };

  const checkout = (price: AppPrice) => {
    setLoadingCheckout(true);
    const url = window.location.href;
    createCheckout({
      variables: {
        input: {
          appPriceId: price.id,
          successUrl: `${url}?stripeSuccess=true`,
          cancelUrl: url,
        },
      },
    })
      .then(({ data }) => {
        if (data?.checkout?.url) {
          window.location.href = data.checkout.url;
        } else {
          setLoadingCheckout(false);
        }
      })
      .catch(() => {
        setLoadingCheckout(false);
      });
  };

  const availableAppPrices = data ? data.availableAppPrices : [];
  const allTieredAppPrices = data ? data.tieredAppPrices : [];
  const premiumAppPrices = data ? data.premiumAppPrices : [];
  const hasStripeId = data && data.currentUser.hasStripeId;
  const currentSubscription = data && data.currentUser.appSubscription;
  const usersCount = currentSubscription
    ? currentSubscription.currentActiveUsersCount
    : 1;

  const lastPrice = chain(allTieredAppPrices).last();

  const showTierChange = !!(
    currentSubscription &&
    currentSubscription.active &&
    currentSubscription.store === 'stripe' &&
    currentSubscription.tiered &&
    currentSubscription.appPrice &&
    !currentSubscription.locked
  );

  const isSubscriptionPastDue =
    currentSubscription?.status === 'subscription-past_due';

  const tierChangePriceOptions =
    showTierChange && currentSubscription && currentSubscription.appPrice
      ? allTieredAppPrices.filter(
          (p) =>
            p.recurring === currentSubscription.appPrice!.recurring &&
            p.id !== currentSubscription.appPrice!.id
        )
      : [];

  return (
    <View>
      {stripeSuccess && !isSubscriptionPastDue && (
        <Alert severity="success" style={{ marginBottom: 16 }}>
          You subscription is now active.
        </Alert>
      )}
      {tierPlanChangeSuccess && (
        <Alert severity="success" style={{ marginBottom: 16 }}>
          {tierPlanChangeSuccess}
        </Alert>
      )}
      {isSubscriptionPastDue && (
        <Alert
          severity="error"
          style={{ marginBottom: 16, justifyContent: 'center' }}
        >
          <View style={{ maxWidth: 400 }}>
            <Paragraph style={{ marginBottom: 12 }}>
              Your subscription payment is past due. Update your payment method
              to keep your plan active and avoid interruption.
            </Paragraph>
            <Button
              mode="contained"
              icon="credit-card-settings"
              color={colors.destructiveColor}
              loading={loadingPortal}
              onPress={openBillingPortal}
            >
              Update payment method
            </Button>
          </View>
        </Alert>
      )}
      <Card>
        <Card.Title title="Billing" />

        <Card.Content>
          <Paragraph>
            <strong>Users with access</strong>: {usersCount}
          </Paragraph>
          {currentSubscription && (
            <>
              {currentSubscription.appPrice && (
                <Paragraph>
                  <strong>Current plan</strong>:{' '}
                  {currentSubscription.appPrice.name} -{' '}
                  {formatPrice(currentSubscription.appPrice)}
                </Paragraph>
              )}
              {currentSubscription.currentPeriodStart &&
                currentSubscription.currentPeriodEnd && (
                  <Paragraph>
                    <strong>Period</strong>:{' '}
                    {moment(currentSubscription.currentPeriodStart).format(
                      'LL'
                    )}{' '}
                    -{' '}
                    {moment(currentSubscription.currentPeriodEnd).format('LL')}
                  </Paragraph>
                )}
              <Paragraph>
                <strong>Status</strong>: {currentSubscription.statusMessage}
              </Paragraph>
              {currentSubscription.locked && (
                <Alert severity="info" style={{ marginTop: 12 }}>
                  Your plan uses locked pricing. Contact support@erg.zone if you
                  need help updating your subscription.
                </Alert>
              )}
            </>
          )}
        </Card.Content>
      </Card>

      {hasStripeId && (
        <Card style={{ marginTop: 10 }}>
          <Card.Content>
            <ListItem
              title="Billing & Receipts"
              description="View payment history, download receipts, update your card, or cancel your plan."
              descriptionNumberOfLines={8}
              onPress={openBillingPortal}
              style={{ paddingHorizontal: 0, paddingVertical: 4 }}
              right={(props) => (
                <View
                  style={[
                    props.style,
                    { justifyContent: 'center', marginLeft: 10 },
                  ]}
                >
                  <Button
                    compact
                    icon="credit-card-settings"
                    mode="contained"
                    loading={loadingPortal}
                    onPress={openBillingPortal}
                  >
                    View details
                  </Button>
                </View>
              )}
            />
          </Card.Content>
        </Card>
      )}

      {showTierChange && hasStripeId && (
        <Card style={{ marginTop: 12 }}>
          <Card.Content>
            <GroupTierChangeSelector
              tieredPrices={tierChangePriceOptions}
              currentAppPrice={currentSubscription!.appPrice!}
              usersCount={usersCount}
              currentPeriodEnd={currentSubscription?.currentPeriodEnd}
              refetchBilling={() => refetch()}
              onTierPlanChangeDetected={setTierPlanChangeSuccess}
              onTierPlanChangeBannerDismiss={() =>
                setTierPlanChangeSuccess(null)
              }
            />
          </Card.Content>
        </Card>
      )}

      {availableAppPrices.length > 0 && (
        <PricesSelector
          premiumPrices={premiumAppPrices}
          prices={availableAppPrices}
          checkout={checkout}
          loadingCheckout={loadingCheckout}
        />
      )}

      <View style={{ marginTop: 20 }}>
        <Subheading>Individual Plan</Subheading>
        <Caption>
          The individual plan unlocks premium features for your own account. The
          options are to pay monthly or annually and they automatically renew at
          the end of the billing cycle.
        </Caption>
        <Caption>
          All individual plans:{'\n'}
          {chain(premiumAppPrices)
            .groupBy('name')
            .map(
              (prices, name) =>
                `${name}: ${prices.map(formatPrice).join(' or ')}`
            )
            .join('\n')
            .value()}
        </Caption>
        <Caption>Additional taxes may apply based on your location.</Caption>
        <Caption>
          <MuiLink
            href="https://help.erg.zone/article/153-what-is-ergzone-plus"
            target="_blank"
            rel="noopener noreferrer"
          >
            Click here
          </MuiLink>{' '}
          to learn more about all ErgZone+ features.
        </Caption>
        <Subheading>Group Plan</Subheading>
        <Caption>
          You choose your group tier on this page. After you review the amount
          from Stripe, confirming applies the change; upgrades may charge your
          card immediately for a prorated difference (when greater than zero),
          while downgrades do not prorate. You can move to a lower tier only
          when your active members (see “Users with access” above) fit within
          that tier. Adding a member beyond your limit is blocked until you
          upgrade on Billing.
        </Caption>
        <Caption>
          Your own user counts toward the limit. Example: Tier 1 (up to 2 users)
          includes you plus one more user.
        </Caption>
        <Caption>
          Annual and monthly options are separate products. Changing from annual
          to monthly billing or vice versa requires contacting support@erg.zone.
          When changing tiers, stay on the same billing period you already have.
        </Caption>
        <Caption>
          You can cancel at any time and you'll only lose access to admin
          features when the billing period ends.
        </Caption>
        <Caption>
          <strong>
            ErgZone+ Premium features (like advanced metrics) are only unlocked
            to the owner of the group.
          </strong>
        </Caption>
        <Caption>
          All groups plans:{'\n'}
          {chain(allTieredAppPrices)
            .groupBy('name')
            .map(
              (prices, name) =>
                `${name}: ${prices.map(formatPrice).join(' or ')}`
            )
            .join('\n')
            .value()}
        </Caption>
        <Caption>{morePlansDisplay(lastPrice)}</Caption>
        <Caption>Additional taxes may apply based on your location.</Caption>
        <Caption>
          Contact us at support@erg.zone for more plans options.
        </Caption>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
    padding: 10,
  },
  card: {
    marginTop: 20,
  },
});

export default BillingPage;
